/* eslint-disable linebreak-style */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useRouter } from 'next/router';
import AppDefaultHeader from 'base-ui/dist/DefaultHeader';
import BrandHeader from 'base-ui/dist/BrandHeader';
import Icon from 'base-ui/dist/Icon';
import { fetchMenu } from 'root/state/slices/menus';
import { useAmp } from 'next/amp';
import { intl } from 'components/LocaleProvider';
import map from 'base-ui/dist/underline/map';
import clone from 'base-ui/dist/underline/clone';
import get from 'base-ui/dist/underline/get';
import { fetchNotifications, markAsRead, markAllAsRead } from 'state/slices/notifications';
import avatarDefault from 'assets/images/user-icon.svg';
import { useAuth } from 'root/hooks/useAuth';
import { useModal } from 'root/hooks/useModal';
import headerSettings from './headerSettings';
import sharedPageConfig from '../page_config';

import messagesAuth from './messages';
import messages from '../Menu/MenuSearch/messages';

const getConfig = require('next/config').default;

const { publicRuntimeConfig = {} } = getConfig ? getConfig() : {};

const DefaultHeader = () => {
  const { handleOpenModal } = useModal();
  const { stateUser, actionsAuth } = useAuth();

  const { user } = stateUser;

  const [session, setSession] = useState({});
  const dispatch = useDispatch();
  const router = useRouter();
  const { items: menus } = useSelector((store) => store.menus);
  const notifications = useSelector((store) => store.notifications.results);
  const currentApps = get(user, 'currentApps', { courses: false, services: false });
  const pageType = publicRuntimeConfig.analytics_datasource;

  useEffect(() => {
    dispatch(fetchMenu('legal'));
    dispatch(fetchMenu('main'));
  }, []);

  useEffect(() => {
    if (user && user._id) {
      dispatch(fetchNotifications());
    }
  }, [user]);

  useEffect(() => {
    const isAdminLogin = Cookies.get('is_admin_login');
    let adminSession = Cookies.get('admin_session');

    if (isAdminLogin && adminSession) {
      adminSession = adminSession.split(' ');
      setSession({
        ...session,
        adminLogin: true,
        adminSession: {
          id: adminSession[0],
          token: adminSession[1],
          displayName: adminSession[2]
        }
      });
    }
  }, []);

  const markAllRead = () => {
    dispatch(markAllAsRead());
  };

  const doRedirect = (notification) => {
    switch (notification.occasion) {
      case 'new_course':
      case 'new_coupon':
      case 'booking_cancellation':
        window.location.href = notification.body.url;
        break;
      default:
        if (notification.body.url) {
          window.location.href = notification.body.url;
        }
        break;
    }
  };

  const openNotification = (notification) => {
    dispatch(markAsRead(notification));

    doRedirect(notification);
  };

  const handleCreatePlaceholder = (type) => {
    const {
      labelSearchHealth,
      labelSearchPets,
      labelSearchTravel,
      labelSearchFitness,
      labelSearchAuto,
      labelSearchGeneric,
    } = messages;
    switch (type) {
      case 'health':
      case 'psychology':
      case 'family':
        return labelSearchHealth;
      case 'animals':
        return labelSearchPets;
      case 'travel':
        return labelSearchTravel;
      case 'fitness':
        return labelSearchFitness;
      case 'auto':
        return labelSearchAuto;
      case 'others':
      case 'decor':
      default:
        return labelSearchGeneric;
    }
  };

  // Set hasAuth in app
  headerSettings.hasAuth = publicRuntimeConfig.modules_auth;

  // Set the default brand img
  headerSettings.brandImg = publicRuntimeConfig.brand_img_mobile;
  headerSettings.brandAlt = publicRuntimeConfig.blogname;
  headerSettings.brandUrl = `${publicRuntimeConfig.PUBLIC_URL}/`;
  headerSettings.logo = {
    img: publicRuntimeConfig.brand_img_mobile,
    alt: publicRuntimeConfig.blogname,
    link: `${publicRuntimeConfig.PUBLIC_URL}/`,
  };
  headerSettings.menus = {
    ...headerSettings.menus,
    fixed: {
      signInLabel: intl.formatMessage(messagesAuth.headerSignInLabel),
      signUpLabel: intl.formatMessage(messagesAuth.headerSignUpLabel),
    },
    auth: {
      signIn: {
        label: intl.formatMessage(messagesAuth.headerSignInLabel),
        action: () => handleOpenModal('signInModal')
      },
      signUp: {
        label: intl.formatMessage(messagesAuth.headerSignUpLabel),
        action: () => handleOpenModal('signUpModal')
      },
      welcome: {
        label: intl.formatMessage(messagesAuth.headerWelcomeLabel),
      },
    },
    account: [
      ...sharedPageConfig({ ...currentApps, type: 'desktop' }).menu.items,
      {
        label: intl.formatMessage(messagesAuth.headerDisconnectLabel),
        // separator: 'before',
        icon: <Icon prefix="fas" name="sign-out-alt" />,
        action: async (evt) => {
          if (get(router, 'pathname') !== '/') {
            router.push(
              { pathname: '/' },
            );
          }
          try {
            evt.preventDefault();
            actionsAuth.signOut();
            Cookies.remove('is_admin_login');
            Cookies.remove('admin_session');
          } catch (e) {
            // eslint-disable-next-line no-console
          }
        },
        routerStrategy: 'none'
      }
    ],
    notification: {
      markAsRead: markAllRead,
      onClick: (notification) => openNotification(notification),
      url: intl.formatMessage(messagesAuth.profileNotificationsURL),
      label: intl.formatMessage(messagesAuth.headerNotificationsLabel),
      counter: notifications.length || Cookies.get('user_notifications'),
      data: notifications
    },
    legal: menus.legal || [],
    main: menus.main ? map(clone(menus.main), (m0) => ({
      ...m0,
      allLabel: intl.formatMessage(messages.allCategory, { category: m0.label }),
      children: m0.children && m0.children.length > 0 ? map(clone(m0.children), (m1) => ({
        ...m1,
        allLabel: intl.formatMessage(messages.allCategory, { category: m1.label }),
        children: m1.children && m1.children.length > 0 ? m1.children : null
      })) : null
    })) : [],
    search: {
      hasSearch: true,
      actionUrl: `${publicRuntimeConfig.PUBLIC_URL}/`,
      placeholderText: intl.formatMessage(handleCreatePlaceholder(pageType))
    }
  };

  if (session?.adminLogin) {
    sharedPageConfig = {
      ...sharedPageConfig,
      menus: {
        ...sharedPageConfig.menus,
        account: [
          {
            label: intl.formatMessage(messages.returnToAdminView, { displayName: session?.adminSession.displayName }),
            action: () => {
              try {
                if (process.browser) {
                  const { token } = session?.adminSession;

                  Cookies.set('user_token', token);
                  Cookies.remove('is_admin_login');
                  Cookies.remove('admin_session');
                  window.location.href = '/';
                }
              } catch (e) {
                // eslint-disable-next-line no-console
              }
            },
            routerStrategy: 'next',
            separator: 'after',
          },
          ...sharedPageConfig.menus.account
        ]
      }
    };
  }

  const brands = headerSettings.brandBar;

  // Verify the brandheader is available
  if (publicRuntimeConfig.header_brand) {
    if (publicRuntimeConfig.header_brand_mcs) {
      brands.push({
        name: 'Mejor con Salud',
        alt: 'Mejor con Salud',
        link: 'https://mejorconsalud.com',
        img: `${publicRuntimeConfig.wp_site_url}/wp-content/themes/base-deploy/assets/img/brands/mejorconsalud.png`,
        icon: `${publicRuntimeConfig.wp_site_url}/wp-content/themes/base-deploy/assets/img/brands/icon-mejorconsalud.png`,
      });
    }

    brands.push({
      name: 'MContigo',
      alt: 'MContigo',
      link: 'https://mcontigo.com',
      img: `${publicRuntimeConfig.wp_site_url}/wp-content/themes/base-deploy/assets/img/brands/mcontigo.png`,
      icon: `${publicRuntimeConfig.wp_site_url}/wp-content/themes/base-deploy/assets/img/brands/icon-mcontigo.png`,
    });
  }

  headerSettings.brandBar = brands.filter((item, index) => brands.findIndex((i) => i.name === item.name) === index);

  return (
    <React.Fragment>
      {publicRuntimeConfig.header_brand && !useAmp() && (
        <BrandHeader settings={headerSettings.brandBar} />
      )}
      <AppDefaultHeader
        // eslint-disable-next-line no-nested-ternary
        user={user && user._id && headerSettings.hasAuth ? {
          name: user.firstName,
          roles: user.roles,
          picture: user.picture || avatarDefault
        } : null}
        settings={headerSettings}
        LinkProp={Link}
      />
    </React.Fragment>
  );
};

export default DefaultHeader;
